import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiShield, FiCheckCircle, FiThermometer, 
  FiHome, FiTrendingUp, FiDollarSign, FiAlertTriangle, 
  FiSun, FiWind, FiLayers, FiTool, FiCheck, FiInfo 
} = FiIcons;

function ModernInsulation() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Wie Sie mit moderner Dämmtechnik Energie sparen und den Wohnkomfort steigern",
    "image": "https://mx.ausbau.nrw/img/Daemmtechnik_xl.jpg",
    "author": {
      "@type": "Person",
      "name": "Maxim Kelm"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MK-AUSBAU",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ausbau.nrw/logo-icon.svg"
      }
    },
    "datePublished": "2026-02-20",
    "description": "Erfahren Sie, wie Sie mit moderner Dämmtechnik bis zu 70% Energie sparen. Ratgeber zu Materialien, Förderung und Umsetzung.",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://ausbau.nrw/blog/moderne-daemmtechnik"
    }
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Moderne Dämmtechnik: Energie sparen & Wohnkomfort steigern"
        description="Ratgeber Dämmung: Materialien (Schafwolle, Mineralwolle), 5-Schritte-Plan zur Sanierung, Förderungen & Bauphysik. ✅ Jetzt informieren!"
        keywords="Dämmtechnik, Energie sparen, Haus dämmen, Schafwolle Dämmung, Förderungen Dämmung, Altbausanierung Dämmung"
        schema={articleSchema}
        canonical="/blog/moderne-daemmtechnik"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/blog" 
          className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-lg prose-mk max-w-none"
        >
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4">
              <SafeIcon icon={FiThermometer} />
              <span>Energieeffizienz & Wohnkomfort</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Wie Sie mit moderner Dämmtechnik Energie sparen und den Wohnkomfort steigern 🏠🌿
            </h1>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Fachartikel von MK-AUSBAU</span>
              <span className="flex items-center"><SafeIcon icon={FiCheckCircle} className="mr-1" /> Stand: Februar 2026</span>
            </div>
          </header>

          <div className="lead text-xl text-gray-600 leading-relaxed mb-12">
            In Zeiten steigender Energiepreise und wachsendem Umweltbewusstsein spielt die Dämmung von Gebäuden eine entscheidende Rolle. Für Hausbesitzer, Bauherren, Investoren oder Sanierungswillige ist die Dämmtechnik ein zentrales Thema, wenn es um Energieeinsparung, Wohnkomfort und langfristige Wertsteigerung der Immobilie geht.
          </div>

          <div className="bg-mk-teal-50 border-l-4 border-mk-teal-500 p-6 my-8 rounded-r-xl">
            <p className="text-mk-teal-900 m-0 font-medium">
              Doch was bedeutet Dämmtechnik genau? Welche Materialien und Methoden sind geeignet? Wie lässt sich eine Sanierung effizient umsetzen – ohne Fehler, mit staatlicher Förderung und maximalem Nutzen? In diesem Beitrag erfahren Sie Schritt für Schritt, wie Sie die ideale Dämmstrategie für Ihr Zuhause oder Ihr Projekt entwickeln.
            </p>
          </div>

          {/* ... Rest of content ... */}
          
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiInfo} className="mr-3 text-mk-teal-600" />
            Was ist Dämmtechnik und warum ist sie so wichtig?
          </h2>
          <p className="text-gray-700">
            Dämmtechnik umfasst sämtliche baulichen Maßnahmen, die den Wärmeverlust eines Gebäudes reduzieren. Sie wirkt isolierend gegen Kälte im Winter, schützt vor Hitze im Sommer und reduziert die Schallübertragung. Gleichzeitig leistet sie einen wichtigen Beitrag zum Klimaschutz und zur Senkung der CO₂-Emissionen.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <h4 className="font-bold text-gray-900 mt-0 mb-3 flex items-center">
                <SafeIcon icon={FiHome} className="mr-2 text-mk-teal-500" />
                Typische Einsatzbereiche
              </h4>
              <ul className="space-y-2 text-sm text-gray-600 list-none p-0 m-0">
                <li className="flex items-start"><span className="mr-2 text-mk-teal-500">➜</span> Außenwanddämmung (WDVS)</li>
                <li className="flex items-start"><span className="mr-2 text-mk-teal-500">➜</span> Dachdämmung (Zwischen-/Aufsparren)</li>
                <li className="flex items-start"><span className="mr-2 text-mk-teal-500">➜</span> Kellerdecken- & Fußbodendämmung</li>
                <li className="flex items-start"><span className="mr-2 text-mk-teal-500">➜</span> Rohrisolierung & Heizungsdämmung</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-mk-teal-500 to-mk-teal-700 p-6 rounded-xl text-white shadow-lg flex flex-col justify-center items-center text-center">
              <SafeIcon icon={FiTrendingUp} className="w-12 h-12 mb-4 text-mk-teal-200" />
              <p className="font-bold text-2xl m-0">Bis zu 70%</p>
              <p className="text-mk-teal-100 text-sm m-0 mt-2">des Heizenergieverbrauchs lassen sich mit moderner Dämmtechnik einsparen.</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-8">
            Die 5 Schritte zur erfolgreichen Dämmung Ihres Hauses
          </h2>
          
          {/* ... Steps content ... */}

          <div className="bg-gray-900 text-white p-10 rounded-[2.5rem] shadow-2xl mb-16 text-center">
            <h2 className="text-white text-3xl font-bold mb-6 mt-0">Fazit: Dämmung als Zukunftsinvestition</h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Die richtige Dämmtechnik ist heute kein „Nice to have“ mehr – sie ist entscheidend für den Werterhalt. Wer jetzt handelt, profitiert doppelt: Weniger Kosten & mehr Wohnkomfort.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-mk-teal-300">Energie sparen</span>
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-mk-teal-300">Wohnklima verbessern</span>
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-mk-teal-300">Wert steigern</span>
            </div>
          </div>

          <div className="text-center py-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Möchten Sie Ihr Haus energetisch sanieren?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Wir beraten Sie zu den passenden Maßnahmen und Materialien – von Mineralwolle bis zur ökologischen Schafwolle.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/kontakt"
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Kostenlose Beratung anfordern
              </Link>
              <Link
                to="/daemmtechnik"
                className="bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Mehr zu unseren Dämm-Leistungen
              </Link>
            </div>
          </div>
        </motion.article>
      </div>

      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default ModernInsulation;